// @ts-strict-ignore
import { Colors, EditableText, Icon, Spinner } from '@blueprintjs/core'
import React, { useEffect, useMemo, useState } from 'react'
import { MorphMaps } from '../config'
import { axiosPost } from '../util/api'

/**
 * Provides a editable text field to change the product's target ACOS.
 *
 * Will inherit the defualt target ACOS from the Settings model
 * and display a placeholder if the product has no target ACOS.
 *
 * @param {any} disabled
 * @param {any} product
 * @param {any} mutateItems
 * @param {any} campaign
 * @param {any} portfolio
 * @return {*}
 * @constructor
 */

export const AllowedTargets = {
    Portfolio: 'portfolio' as 'portfolio',
    Campaign: 'campaign' as 'campaign',
    AdGroup: 'ad_group' as 'ad_group',
    Target: 'targets' as 'targets',
    Keyword: 'keywords' as 'keywords',
}
export type AllowedTargets = (typeof AllowedTargets)[keyof typeof AllowedTargets]

type TargetACOSProps = {
    disabled?: boolean
    product: Product
    // product: Product
    mutateItems?: (i) => void
    target: AllowedTargets
}

const getMorphMap = (target: AllowedTargets): number | null => {
    switch (target) {
        case AllowedTargets.Campaign:
            return MorphMaps.CAMPAIGN
        case AllowedTargets.Portfolio:
            return MorphMaps.Portfolios
        case AllowedTargets.AdGroup:
            return MorphMaps.AD_GROUP
        case AllowedTargets.Target:
            return MorphMaps.TARGETS
        case AllowedTargets.Keyword:
            return MorphMaps.KEYWORD
        default:
            return null
    }
}

type Product = {
    target_acos: number
    default_target_acos: number
    asin: string
    campaign_id: number
    ad_group_id: number
    id: number
    target_acos_description?: string
    target_acos_parent_type_id: number
    target_id: number
    keyword_id: number
}

export const TargetACOS = React.memo(
    ({ disabled, product, mutateItems, target }: TargetACOSProps) => {
        const hasOwnTarget: boolean = useMemo(() => {
            return (
                !!product?.target_acos &&
                product?.target_acos_parent_type_id === getMorphMap(target)
            )
        }, [product?.target_acos, product?.target_acos_parent_type_id])

        const original: string = useMemo(() => {
            const result = product?.target_acos
                ? product?.target_acos / 100
                : product?.default_target_acos / 100
            return result.toString()
        }, [product])

        const [value, setValue] = useState<string>(original)
        const [loading, setLoading] = useState<boolean>(false)
        const [result, setResult] = useState(null)
        const [editing, setEditing] = useState<boolean>(false)

        useEffect(() => {
            if (value != original) {
                setValue(original)
            }
        }, [original])

        const resolvedTarget = useMemo(() => {
            return resolveTarget(product)
        }, [product?.target_acos, product?.target_acos_parent_type_id])

        const [parentTypeId, parentId] = useMemo(() => {
            return resolveParents(product, target)
        }, [original])

        const update = (targetAcos) => {
            setEditing(false)
            setLoading(true)
            setValue(targetAcos ? targetAcos.toString() : '')
            axiosPost('target-acos', {
                target_acos: targetAcos !== '' ? targetAcos : undefined,
                target_acos_delete: targetAcos !== '' ? undefined : 'true',
                asin: undefined,
                parent_id: parentId,
                parent_type_id: parentTypeId,
            })
                .then((res) => {
                    setLoading(false)
                    setResult('success')
                    // setHasOwnTarget(true) // TODO check this

                    const tacos = res.data.target_acos
                    setValue(tacos ? (tacos / 100).toString() : '0')

                    if (mutateItems) {
                        console.log('mutate items', mutateItems)
                        mutateItems((items) => {
                            console.log('items', items)
                            // Update our local store to the new tacos
                            return items.map((item) => {
                                // Skip any items we didn't modify
                                if (!itemMatches(item, parentId, target)) {
                                    return item
                                }

                                return {
                                    ...item,
                                    target_acos_parent_type_id: res.data.parent_type_id,
                                    target_acos_parent_id: res.data.parent_id,
                                    target_acos: res.data.target_acos,
                                }
                            })
                        })
                    }
                })
                .catch((res) => {
                    setLoading(false)
                    setResult('error')
                    setValue(original)
                })
        }

        return (
            <div
                className="d-flex"
                title={
                    product.target_acos_description
                        ? product.target_acos_description
                        : `Using ${resolvedTarget} level target ACOS`
                }
            >
                <EditableText
                    disabled={disabled}
                    type="number"
                    className="mr-1"
                    minWidth={54}
                    placeholder={!hasOwnTarget ? original + '%' : undefined}
                    value={editing ? value : hasOwnTarget ? value + '%' : ''}
                    onChange={(e) => setValue(e.replace('%', ''))}
                    onConfirm={update}
                    onEdit={() => setEditing(true)}
                    onCancel={() => setEditing(false)}
                />
                {!loading && result === 'success' && (
                    <FadeOut>
                        <Icon icon={<Icon icon="tick" color={Colors.GREEN3} />} />
                    </FadeOut>
                )}
                {!loading && result === 'error' && (
                    <FadeOut>
                        <Icon icon={<Icon icon="cross" color={Colors.RED3} />} />
                    </FadeOut>
                )}
                {loading && <Spinner size={14} />}
            </div>
        )
    },
)

interface TargetACOSProps2 {
    disabled?: boolean
    product: TargetAcosProps
    mutateItems?: (i) => void
}

export type TargetAcosProps = {
    target_acos?: number
    default_target_acos: number
    target_acos_description?: string
    target_acos_parent_type_id: number
    target_acos_parent_id: number
} & (
    | {
          target: 'campaign'
          campaign_id: number
      }
    | {
          target: 'ad_group'
          ad_group_id: number
          campaign_id: number
      }
    | {
          target: 'targets'
          target_id: number
          ad_group_id: number
          campaign_id: number
      }
    | {
          target: 'keywords'
          keyword_id: number
          ad_group_id: number
          campaign_id: number
      }
    | {
          target: 'portfolio'
          id: number
      }
)

export const TargetACOS2 = React.memo(({ disabled, product, mutateItems }: TargetACOSProps2) => {
    const hasOwnTarget: boolean = useMemo(() => {
        return (
            !!product?.target_acos &&
            product?.target_acos_parent_type_id === getMorphMap(product.target)
        )
    }, [product?.target_acos, product?.target_acos_parent_type_id])

    const original: string = useMemo(() => {
        const result = product?.target_acos
            ? product?.target_acos / 100
            : product?.default_target_acos / 100
        return result.toString()
    }, [product])

    const [value, setValue] = useState<string>(original)
    const [loading, setLoading] = useState<boolean>(false)
    const [result, setResult] = useState(null)
    const [editing, setEditing] = useState<boolean>(false)

    useEffect(() => {
        if (value != original) {
            setValue(original)
        }
    }, [original])

    const resolvedTarget = useMemo(() => {
        return resolveTarget(product)
    }, [product?.target_acos, product?.target_acos_parent_type_id])

    const [parentTypeId, parentId] = useMemo(() => {
        return resolveParents(product, product.target)
    }, [original])

    const update = (targetAcos) => {
        setEditing(false)
        setLoading(true)
        setValue(targetAcos ? targetAcos.toString() : '')
        axiosPost('target-acos', {
            target_acos: targetAcos !== '' ? targetAcos : undefined,
            target_acos_delete: targetAcos !== '' ? undefined : 'true',
            asin: undefined,
            parent_id: parentId,
            parent_type_id: parentTypeId,
        })
            .then((res) => {
                setLoading(false)
                setResult('success')
                // setHasOwnTarget(true) // TODO check this

                const tacos = res.data.target_acos
                setValue(tacos ? (tacos / 100).toString() : '0')

                if (mutateItems) {
                    console.log('mutate items', mutateItems)
                    mutateItems((items) => {
                        console.log('items', items)
                        // Update our local store to the new tacos
                        return items.map((item) => {
                            // Skip any items we didn't modify
                            if (!itemMatches(item, parentId, product.target)) {
                                return item
                            }

                            return {
                                ...item,
                                target_acos_parent_type_id: res.data.parent_type_id,
                                target_acos_parent_id: res.data.parent_id,
                                target_acos: res.data.target_acos,
                            }
                        })
                    })
                }
            })
            .catch((res) => {
                setLoading(false)
                setResult('error')
                setValue(original)
            })
    }

    return (
        <div
            className="d-flex"
            title={
                product.target_acos_description
                    ? product.target_acos_description
                    : `Using ${resolvedTarget} level target ACOS`
            }
        >
            <EditableText
                disabled={disabled}
                type="number"
                className="mr-1"
                minWidth={54}
                placeholder={!hasOwnTarget ? original + '%' : undefined}
                value={editing ? value : hasOwnTarget ? value + '%' : ''}
                onChange={(e) => setValue(e.replace('%', ''))}
                onConfirm={update}
                onEdit={() => setEditing(true)}
                onCancel={() => setEditing(false)}
            />
            {!loading && result === 'success' && (
                <FadeOut>
                    <Icon icon={<Icon icon="tick" color={Colors.GREEN3} />} />
                </FadeOut>
            )}
            {!loading && result === 'error' && (
                <FadeOut>
                    <Icon icon={<Icon icon="cross" color={Colors.RED3} />} />
                </FadeOut>
            )}
            {loading && <Spinner size={14} />}
        </div>
    )
})

const resolveTarget = (product: any): string => {
    switch (product.target_acos_parent_type_id) {
        case MorphMaps.KEYWORD:
            return 'keywords'
        case MorphMaps.TARGETS:
            return 'targets'
        case MorphMaps.AD_GROUP:
            return 'ad group'
        case MorphMaps.CAMPAIGN:
            return 'campaign'
        case MorphMaps.Portfolios:
            return 'portfolio'
        default:
            return 'account'
    }
}

const resolveParents = (product: any, target: AllowedTargets): [number, number] => {
    switch (target) {
        case AllowedTargets.Target:
            return [MorphMaps.TARGETS, product.target_id]
        case AllowedTargets.Keyword:
            return [MorphMaps.KEYWORD, product.keyword_id]
        case AllowedTargets.AdGroup:
            return [MorphMaps.AD_GROUP, product.ad_group_id]
        case AllowedTargets.Campaign:
            return [MorphMaps.CAMPAIGN, product.campaign_id]
        case AllowedTargets.Portfolio:
            return [MorphMaps.Portfolios, product.id]
    }
}

export const itemMatches = (item, parentId, target): boolean => {
    switch (target) {
        case AllowedTargets.Portfolio:
            return item.id === parentId
        case AllowedTargets.Campaign:
            return item.campaign_id === parentId
        case AllowedTargets.AdGroup:
            return item.ad_group_id === parentId
        case AllowedTargets.Target:
            return item.target_id === parentId
        case AllowedTargets.Keyword:
            return item.keyword_id === parentId
        default:
            console.warn('Unhandled target type: ' + target)
            return false
    }
}

export const FadeOut = ({ children, after = 1000 }) => {
    const [show, setShow] = useState(true)
    useEffect(() => {
        const timeout = setTimeout(() => {
            setShow(false)
        }, after)
        return () => clearTimeout(timeout)
    }, [])

    return <div className={show ? '' : 'fade-out'}>{children}</div>
}
